import type { CSSProperties, FC, ReactElement } from "react"

import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"

import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { useCSSInsertion } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"

import { waveLoaderConfig } from "./wave-loader.config"
import { WaveLoaderIcon as Icon } from "./wave-loader.icon"
import { EWaveLoaderVariant as EVariant } from "./wave-loader.types"

const { getWaveLoaderAnimationClasses: getAnimation } = waveLoaderConfig

type TProps = {
    cssIdentifier: string
    variant?: EVariant
    withRelativePosition?: boolean
    title?: string
    titleHint?: string
    wrapperClassName?: string
    loaderClassName?: string
    width?: CSSProperties["width"]
}

const classes = {
    loaderWrapper: ({ isRelative }: { isRelative: boolean }): string =>
        buildCssClassName(
            "flex",
            "flex-col",
            "gap-y-[10px]",
            isRelative ? ["relative"] : ["absolute", "top-1/2", "left-1/2", "-translate-x-1/2", "-translate-y-1/2"]
        ),
    loader: (): string => "m-auto",
    textWrapper: (): string => "flex flex-col gap-y-[5px]",
    text: (): string => "select-none"
}

const WaveLoader: FC<TProps> = ({
    cssIdentifier,
    variant = EVariant.WhiteBlue,
    withRelativePosition = false,
    title = undefined,
    titleHint = undefined,
    wrapperClassName = String(""),
    loaderClassName = String(""),
    width = "100%"
}: TProps): ReactElement => {
    useCSSInsertion({ cssString: getAnimation(cssIdentifier, variant) })

    const isTitlePresent: boolean = !isUndefined(title) && isString(title),
        isHintPresent: boolean = !isUndefined(titleHint) && isString(titleHint)

    return (
        <div
            style={{ width }}
            className={buildCssClassName(classes.loaderWrapper({ isRelative: withRelativePosition }), {
                [wrapperClassName]: wrapperClassName
            })}
        >
            <Icon
                animationCssIdentifier={cssIdentifier}
                className={buildCssClassName(classes.loader(), { [loaderClassName]: loaderClassName })}
            />

            {(isTitlePresent || isHintPresent) && (
                <div className={classes.textWrapper()}>
                    {isTitlePresent && (
                        <Typography
                            text={title}
                            className={classes.text()}
                            tag={ETypographyTag.Paragraph}
                            color={ETypographyColor.Dark}
                            alignment={ETypographyAlignment.Center}
                            weight={ETypographyWeight.Bold}
                        />
                    )}
                    {isHintPresent && (
                        <Typography
                            text={titleHint}
                            className={classes.text()}
                            tag={ETypographyTag.Span}
                            color={ETypographyColor.Dark}
                            alignment={ETypographyAlignment.Center}
                            size={ETypographySize.Tiny}
                            isItalic
                        />
                    )}
                </div>
            )}
        </div>
    )
}

WaveLoader.displayName = "WaveLoader"

export { WaveLoader }
